import { Select } from "@chakra-ui/react";
import InlineErrorState from "../../../components/states/InlineErrorState";
import InlineScopePrompt from "../../_shared/InlineScopePrompt";
import { translate } from "../../../i18n";
import { useGetCourts } from "../../court-management/hooks/useGetCourts";
import { useSelectedLocation } from "../../location-management/hooks/useSelectedLocation";
import { isUnreadable } from "../../analytics/utils/statValue";

interface CourtPickerProps {
    selectedCourt: number;
    setSelectedCourt: (courtId: number) => void;
}

export default function CourtPicker({ selectedCourt, setSelectedCourt }: CourtPickerProps) {
    const { selectedLocation } = useSelectedLocation();
    const { data: courts, isError, state } = useGetCourts(selectedLocation?.id);

    // A refused or failed read leaves `courts` undefined, which rendered as a
    // Select holding nothing but its placeholder — indistinguishable from a
    // venue that genuinely has no courts. Say which one it is (R89-4 / D57).
    //
    // Paired with the data through `isUnreadable`, not the raw flag (R132-4):
    // React Query KEEPS `courts` from the last success through a failed
    // refetch, and `useUpdateCourt` invalidates this key — so an operator who
    // edits a court while the network is flaky had this picker REPLACED by an
    // error block, losing the ability to pick at all over a refresh nobody
    // asked for. `isError` alone is only the right question when there is
    // nothing on screen to keep.
    if (isUnreadable(isError, courts)) {
        return <InlineErrorState message={translate('courts.error_loading')} />;
    }

    // The read was never ISSUED, because no club is selected (R110-6 / R133-4).
    // Until run 134 `useGetCourts` had no `enabled:` gate, so the request went
    // out as `courts?locationId=undefined`, the API answered 200 with `[]`, and
    // this rendered as a Select holding nothing but its placeholder — the same
    // false "this venue has no courts" the error arm above exists to prevent,
    // reached through a successful response instead of a refused one.
    if (state === 'never-issued') {
        return <InlineScopePrompt gate="location" />;
    }

    return (
        <Select placeholder={translate('courts.select_court')} value={selectedCourt} onChange={(e) => setSelectedCourt(Number.parseInt(e.target.value))}>
            {courts?.map((court) => (
                <option key={court.id} value={court.id}>
                    {court.name}
                </option>
            ))}
        </Select>
    );
}
